import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';

/**
 * Thin coordinator store for the SOW form.
 *
 * Field validation (serviceTier, sowTimeframe, description) has moved to
 * react-hook-form inside SOWFormContainer. This store exists only to:
 *  - Hold serviceId / serviceName (set from router state by SowPageLayout)
 *  - Hold uploadedFiles state + file operation actions
 *  - Expose isValid to SowPageLayout (for submit button gating)
 *  - Expose formData to SowPageLayout (for building the submission FormData)
 */

export interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: string;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  file?: File;
}

export interface SOWFormData {
  serviceId: string;
  serviceName: string;
  serviceTier: string;
  sowTimeframe: string;
  description: string;
  uploadedFiles: UploadedFile[];
}

interface SOWFormState extends BaseStoreState {
  formData: SOWFormData;
  isValid: boolean;
}

export interface SOWFormStore extends SOWFormState {
  setServiceId: (_serviceId: string) => void;
  setServiceName: (_serviceName: string) => void;
  setValidity: (_isValid: boolean) => void;
  setFormFields: (
    _fields: Partial<
      Pick<SOWFormData, 'serviceTier' | 'sowTimeframe' | 'description'>
    >
  ) => void;
  addUploadedFile: (_file: UploadedFile) => void;
  removeUploadedFile: (_fileId: string) => void;
  updateFileProgress: (_fileId: string, _progress: number) => void;
  updateFileStatus: (_fileId: string, _status: UploadedFile['status']) => void;
  updateFile: (_fileId: string, _updates: Partial<UploadedFile>) => void;
  resetForm: () => void;
  clearStore: () => void;
}

const initialFormData: SOWFormData = {
  serviceId: '',
  serviceName: '',
  serviceTier: '',
  sowTimeframe: '',
  description: '',
  uploadedFiles: [],
};

export const useSOWFormStore = createPersistedStore<SOWFormState>(
  'sow-form',
  {
    formData: initialFormData,
    isValid: false,
  },
  (set, get) => ({
    setServiceId: (serviceId: string) =>
      set((state: SOWFormState) => ({
        formData: { ...state.formData, serviceId },
      })),

    setServiceName: (serviceName: string) =>
      set((state: SOWFormState) => ({
        formData: { ...state.formData, serviceName },
      })),

    setValidity: (isValid: boolean) => set({ isValid }),

    // Synced from RHF watch() in SOWFormContainer
    setFormFields: (
      fields: Partial<
        Pick<SOWFormData, 'serviceTier' | 'sowTimeframe' | 'description'>
      >
    ) =>
      set((state: SOWFormState) => ({
        formData: { ...state.formData, ...fields },
      })),

    addUploadedFile: (file: UploadedFile) =>
      set((state: SOWFormState) => ({
        formData: {
          ...state.formData,
          uploadedFiles: [...state.formData.uploadedFiles, file],
        },
      })),

    removeUploadedFile: (fileId: string) =>
      set((state: SOWFormState) => ({
        formData: {
          ...state.formData,
          uploadedFiles: state.formData.uploadedFiles.filter(
            (f: UploadedFile) => f.id !== fileId
          ),
        },
      })),

    updateFileProgress: (fileId: string, progress: number) =>
      set((state: SOWFormState) => ({
        formData: {
          ...state.formData,
          uploadedFiles: state.formData.uploadedFiles.map((f: UploadedFile) =>
            f.id === fileId ? { ...f, progress } : f
          ),
        },
      })),

    updateFileStatus: (fileId: string, status: UploadedFile['status']) =>
      set((state: SOWFormState) => ({
        formData: {
          ...state.formData,
          uploadedFiles: state.formData.uploadedFiles.map((f: UploadedFile) =>
            f.id === fileId ? { ...f, status } : f
          ),
        },
      })),

    updateFile: (fileId: string, updates: Partial<UploadedFile>) => {
      const { formData } = get();
      set({
        formData: {
          ...formData,
          uploadedFiles: formData.uploadedFiles.map((f: UploadedFile) =>
            f.id === fileId ? { ...f, ...updates } : f
          ),
        },
      });
    },

    resetForm: () => {
      set({ formData: initialFormData, isValid: false });
    },
  }),
  {
    partialize: (state: SOWFormState) => ({
      formData: {
        ...state.formData,
        uploadedFiles: [], // File objects cannot be serialized
      },
      isValid: state.isValid,
    }),
  }
) as unknown as PersistedStoreHook<SOWFormStore>;

export const useSOWFormData = () => useSOWFormStore((state) => state.formData);
export const useSOWErrors = () =>
  ({}) as Partial<Record<keyof SOWFormData, string>>;
export const useSOWIsValid = () => useSOWFormStore((state) => state.isValid);
export const useSOWIsDirty = () => false;
export const useSOWLoading = () => useSOWFormStore((state) => state.loading);
